import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { seoArticles } from "@/mocks/articles";
import type { ArticleData } from "@/mocks/articles";

const SITE_DOMAIN = import.meta.env.VITE_SITE_URL as string;

interface ArticleLayoutProps {
  article: ArticleData;
  backLabel?: string;
}

export default function ArticleLayout({ article, backLabel = "Strona główna" }: ArticleLayoutProps) {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get 3 related articles (exclude current)
  const relatedArticles = seoArticles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="w-full pt-24 pb-12 px-4 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Strona główna
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <Link to="/#artykuly" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Artykuły SEO
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-700 font-medium truncate max-w-[200px] md:max-w-none">
                {article.title}
              </span>
            </nav>

            {/* Label */}
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
              Kakobuy Spreadsheet
            </span>

            {/* H1 */}
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-[1.12] mb-4">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
              <span>
                <i className="ri-user-line mr-1"></i>
                Polski ekspert zakupowy
              </span>
              <span>
                <i className="ri-calendar-line mr-1"></i>
                Ostatnia aktualizacja: czerwiec 2026
              </span>
              <span>
                <i className="ri-time-line mr-1"></i>
                {article.readTime}
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
              {article.description}
            </p>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden mb-6">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="w-full py-10 px-4 md:px-10 bg-background-50">
          <div
            className="max-w-3xl mx-auto text-foreground-800 leading-relaxed space-y-6 prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>

        {/* CTA Block */}
        <section className="w-full py-8 px-4 md:px-10 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background-50 rounded-2xl border border-background-200/80 p-6 md:p-8 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                Gotowy, aby zacząć zakupy?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Odwiedź Kakobuy Spreadsheet i odkryj setki sprawdzonych produktów z Chin w najlepszych cenach.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600"
                >
                  kakobuy spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap border border-foreground-300 text-foreground-700 hover:bg-background-100"
                >
                  {backLabel}
                  <i className="ri-home-4-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="w-full py-12 px-4 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-8 text-center">
              Powiązane artykuły
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/artykul/${ra.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/80 overflow-hidden cursor-pointer hover:border-background-300 transition-all"
                >
                  <div className="aspect-[3/2] overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      title={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors mb-1 line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{ra.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "author": {
                "@type": "Person",
                "name": "Polski ekspert zakupowy",
                "description": "Polski ekspert zakupowy z ponad 5-letnim doświadczeniem w zakupach z Chin"
              },
              "datePublished": "2026-06-21",
              "dateModified": "2026-06-21",
              "inLanguage": "pl-PL",
              "wordCount": article.content.split(/\s+/).length,
              "publisher": {
                "@type": "Organization",
                "name": "Kakobuy Yachts",
                "url": SITE_DOMAIN
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${SITE_DOMAIN}/artykul/${article.slug}`
              },
              "description": article.description,
              "keywords": article.keywords.join(", "),
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Strona główna",
                  "item": `${SITE_DOMAIN}/`
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Artykuły SEO",
                  "item": `${SITE_DOMAIN}/#artykuly`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": article.title,
                  "item": `${SITE_DOMAIN}/artykul/${article.slug}`
                }
              ]
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}